import React, { createContext, useContext, useState, useCallback } from 'react';
import { apiGet, apiPost, apiPatch, apiDelete, apiUpload } from '../utils/api';
import { generateChatTitle } from '../utils/titleGenerator';

const KprosContext = createContext(null);

export function KprosProvider({ children }) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('tips');

  // Tips
  const [tips, setTips] = useState([]);
  const [tipsLoading, setTipsLoading] = useState(false);

  // Conversations
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [conversationsLoading, setConversationsLoading] = useState(false);

  // Saved Prompts
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [savedPromptsLoading, setSavedPromptsLoading] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState(null);

  const fetchTips = useCallback(async (filters = {}) => {
    setTipsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.feature_tag) params.append('feature_tag', filters.feature_tag);
      const query = params.toString();
      const res = await apiGet(`/kpros/tips${query ? `?${query}` : ''}`);
      if (res.ok) {
        const data = await res.json();
        setTips(data.tips || []);
      }
    } catch (err) {
      console.error('Failed to fetch tips:', err);
    } finally {
      setTipsLoading(false);
    }
  }, []);

  const fetchConversations = useCallback(async () => {
    setConversationsLoading(true);
    try {
      const res = await apiGet('/kpros/conversations');
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
      }
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  const fetchMessages = useCallback(async (conversationId) => {
    try {
      const res = await apiGet(`/kpros/conversations/${conversationId}/messages`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    }
  }, []);

  const selectConversation = useCallback(async (conversationId) => {
    setCurrentConversationId(conversationId);
    if (conversationId) {
      await fetchMessages(conversationId);
    } else {
      setMessages([]);
    }
  }, [fetchMessages]);

  const sendChat = useCallback(async (message, currentPage, file = null) => {
    setChatLoading(true);
    try {
      const formData = new FormData();
      formData.append('message', message);
      if (currentConversationId) formData.append('conversation_id', currentConversationId);
      if (currentPage) formData.append('current_page', currentPage);
      if (file) formData.append('file', file);

      // Use the shared apiUpload helper so the base URL comes from getConfig()
      // (baked Azure URL when VITE_API_URL is unset) instead of a localhost
      // fallback, and so token refresh / auth are handled consistently.
      const res = await apiUpload('/kpros/chat', formData);

      if (res.ok) {
        const data = await res.json();
        const isNewConversation = data.conversation_id && data.conversation_id !== currentConversationId;
        if (isNewConversation) {
          setCurrentConversationId(data.conversation_id);
          fetchConversations();
        }
        const userMsg = { role: 'user', content: message, created_at: new Date().toISOString() };
        if (file) userMsg.fileName = file.name;
        setMessages(prev => [
          ...prev,
          userMsg,
          { role: 'assistant', content: data.content, citations: data.citations, created_at: new Date().toISOString() },
        ]);

        // Auto-generate title for new conversations
        if (isNewConversation && data.content) {
          generateChatTitle({ userMessage: message, aiResponse: data.content })
            .then(title => {
              if (title && data.conversation_id) {
                apiPatch(`/kpros/conversations/${data.conversation_id}`, { title });
                setConversations(prev => prev.map(c =>
                  c.id === data.conversation_id ? { ...c, title } : c
                ));
              }
            })
            .catch(() => {}); // silent fail — truncated title is already set server-side
        }

        return data;
      }
    } catch (err) {
      console.error('Failed to send chat:', err);
    } finally {
      setChatLoading(false);
    }
  }, [currentConversationId, fetchConversations]);

  const renameConversation = useCallback(async (id, title) => {
    try {
      const res = await apiPatch(`/kpros/conversations/${id}`, { title });
      if (res.ok) {
        setConversations(prev => prev.map(c => c.id === id ? { ...c, title } : c));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to rename conversation:', err);
      return false;
    }
  }, []);

  const deleteConversation = useCallback(async (id) => {
    try {
      const res = await apiDelete(`/kpros/conversations/${id}`);
      if (res.ok) {
        setConversations(prev => prev.filter(c => c.id !== id));
        if (currentConversationId === id) {
          setCurrentConversationId(null);
          setMessages([]);
        }
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  }, [currentConversationId]);

  const startNewConversation = useCallback(() => {
    setCurrentConversationId(null);
    setMessages([]);
  }, []);

  const reportIssue = useCallback(async ({ title, description, severity, page_url, attachments }) => {
    try {
      let res;
      if (attachments && attachments.length > 0) {
        const formData = new FormData();
        formData.append('title', title);
        formData.append('description', description);
        formData.append('severity', severity);
        formData.append('page_url', page_url);
        attachments.forEach(file => formData.append('attachments[]', file));
        res = await apiUpload('/kpros/report_issue', formData);
      } else {
        res = await apiPost('/kpros/report_issue', { title, description, severity, page_url });
      }
      return res.ok;
    } catch (err) {
      console.error('Failed to report issue:', err);
      return false;
    }
  }, []);

  const submitTip = useCallback(async ({ title, body, category, feature_tag }) => {
    try {
      const res = await apiPost('/kpros/submit_tip', { title, body, category, feature_tag });
      return res.ok;
    } catch (err) {
      console.error('Failed to submit tip:', err);
      return false;
    }
  }, []);

  // ── Saved Prompts ──
  const fetchSavedPrompts = useCallback(async (filters = {}) => {
    setSavedPromptsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.feature_tag) params.append('feature_tag', filters.feature_tag);
      if (filters.search) params.append('search', filters.search);
      const query = params.toString();
      const res = await apiGet(`/kpros/saved_prompts${query ? `?${query}` : ''}`);
      if (res.ok) {
        const data = await res.json();
        setSavedPrompts(data.prompts || []);
      }
    } catch (err) {
      console.error('Failed to fetch saved prompts:', err);
    } finally {
      setSavedPromptsLoading(false);
    }
  }, []);

  const savePrompt = useCallback(async ({ title, content, feature_tag }) => {
    try {
      const res = await apiPost('/kpros/saved_prompts', { prompt: { title, content, feature_tag } });
      if (res.ok) {
        const data = await res.json();
        setSavedPrompts(prev => [data.prompt, ...prev]);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to save prompt:', err);
      return false;
    }
  }, []);

  const updateSavedPrompt = useCallback(async (id, { title, content, feature_tag }) => {
    try {
      const res = await apiPatch(`/kpros/saved_prompts/${id}`, { prompt: { title, content, feature_tag } });
      if (res.ok) {
        const data = await res.json();
        setSavedPrompts(prev => prev.map(p => p.id === id ? data.prompt : p));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to update prompt:', err);
      return false;
    }
  }, []);

  const deleteSavedPrompt = useCallback(async (id) => {
    try {
      const res = await apiDelete(`/kpros/saved_prompts/${id}`);
      if (res.ok) {
        setSavedPrompts(prev => prev.filter(p => p.id !== id));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to delete prompt:', err);
      return false;
    }
  }, []);

  return (
    <KprosContext.Provider value={{
      panelOpen, setPanelOpen,
      activeTab, setActiveTab,
      tips, tipsLoading, fetchTips,
      conversations, conversationsLoading, fetchConversations,
      currentConversationId, selectConversation, startNewConversation, renameConversation, deleteConversation,
      messages, chatLoading, sendChat,
      reportIssue, submitTip,
      savedPrompts, savedPromptsLoading, fetchSavedPrompts,
      savePrompt, updateSavedPrompt, deleteSavedPrompt,
      pendingPrompt, setPendingPrompt,
    }}>
      {children}
    </KprosContext.Provider>
  );
}

export function useKpros() {
  const context = useContext(KprosContext);
  if (!context) throw new Error('useKpros must be used within KprosProvider');
  return context;
}

export default KprosContext;
